import { useMemo } from "react";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import {
  ReportType,
  SettingsPeriod,
  ReportingAction,
  ReportingBreakdown
} from "./types";
import { transformApiResponseToComponentData } from "@/components/layouts/PipelineOverTime/helpers";
import {
  PipelineDimension,
  PipelinePeriod,
  PipelineValueOverTimeResponse
} from "@/components/layouts/PipelineOverTime/types";

export const useReport = (
  reportType: ReportType,
  period?: SettingsPeriod,
  queryOptions?: { staleTime?: number }
) => {
  const result = useAPI(
    "reports/type",
    {
      pathParams: { type: reportType.toString() },
      queryParams: period ? { period: period.toString() } : undefined
    },
    { retry: false, staleTime: queryOptions?.staleTime ?? 0 }
  );

  return useMemo(
    () => ({
      report: result.data,
      error: result.error,
      isLoading: result.isLoading,
      isSuccess: result.isSuccess,
      refetch: result.refetch
    }),
    [
      result.data,
      result.error,
      result.isLoading,
      result.isSuccess,
      result.refetch
    ]
  );
};

export const usePipelineValueOverTime = (
  period: PipelinePeriod,
  page: number = 0
) => {
  const dimension =
    period === PipelinePeriod.MONTHLY
      ? PipelineDimension.MONTH
      : PipelineDimension.QUARTER;

  const mutation = useAPIMutation<
    { action: string; dimension: string; page: number; breakdownBy: string },
    PipelineValueOverTimeResponse
  >("analytics/fetchWithBreakdowns", { method: "POST" });

  const fetchData = () => {
    mutation.mutate({
      action: ReportingAction.PIPELINE_VALUE_OVER_TIME,
      dimension,
      page,
      breakdownBy: ReportingBreakdown.NONE
    });
  };

  const transformedData = useMemo(() => {
    if (!mutation.data) return null;

    const dataPoints = transformApiResponseToComponentData(mutation.data);

    return {
      pipelineOverTime: {
        dataPoints,
        snapshotDate: new Date().toISOString(),
        totalDeals: dataPoints.reduce(
          (sum, p) => sum + p.pipeline.dealsCount,
          0
        ),
        totalValue: dataPoints.reduce((sum, p) => sum + p.pipeline.value, 0),
        period: period as PipelinePeriod
      },
      pagination: {
        currentPage: mutation.data.currentPage,
        pageSize: mutation.data.pageSize,
        totalPages: mutation.data.totalPages,
        prevPages: mutation.data.prevPages,
        nextPages: mutation.data.nextPages
      },
      rawApiResponse: mutation.data
    };
  }, [mutation.data, period]);

  return {
    data: transformedData,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    fetchData
  };
};
